<template>
  <div class="pos-invoice-sidebar fill-height d-flex flex-column white rounded-lg elevation-1 ma-2 pa-4">

    <!-- Header CUPOM -->
    <div class="d-flex align-center justify-space-between mb-4">
      <h2 class="text-h6 font-weight-bold grey--text text--darken-3">CUPOM</h2>
      <span class="caption grey--text">#{{ invoiceNumber }}</span>
    </div>

    <!-- Customer Section -->
    <div class="mb-4">
      <div class="d-flex align-center">
        <v-avatar size="32" color="grey lighten-3" class="mr-2">
           <img 
             v-if="selectedCustomer && selectedCustomer.avatar" 
             :src="selectedCustomer.avatar" 
           />
           <span v-else class="font-weight-bold grey--text text--darken-2">
             {{ selectedCustomer ? selectedCustomer.name.charAt(0) : '?' }}
           </span>
        </v-avatar>
        
        <v-autocomplete
          :value="selectedCustomer"
          @input="$emit('update:selectedCustomer', $event)"
          :items="customerResults"
          :loading="customerLoading"
          :search-input.sync="customerSearchInput"
          placeholder="Buscar Cliente..."
          item-text="name"
          item-value="id"
          return-object
          outlined
          dense
          hide-details
          class="flex-grow-1 rounded-lg"
          @update:search-input="$emit('search-customer', $event)"
        ></v-autocomplete>
        
        <v-btn 
          icon 
          small 
          class="ml-1" 
          color="primary"
          @click="$emit('open-customer-modal')"
        >
          <v-icon>{{ icons.mdiAccountPlus }}</v-icon>
        </v-btn>
      </div>
      <div class="d-flex justify-space-between mt-2" v-if="selectedCustomer">
         <span class="caption grey--text">ID: #{{ selectedCustomer.id }}</span>
         <span class="caption blue--text pointer" @click="$emit('update:selectedCustomer', null)">Trocar Usuário</span>
      </div>
    </div>

    <v-divider class="mb-6"></v-divider>

    <!-- Payment Summary -->
    <div class="mb-6 flex-grow-1">
      <div class="d-flex justify-space-between mb-2">
        <span class="grey--text text--darken-1">Subtotal</span>
        <span class="font-weight-bold">{{ (subtotal / 100) | moneyBr }}</span>
      </div>
      
      <!-- Promo/Discount -->
      <div class="d-flex align-center justify-space-between mb-2">
        <span class="grey--text text--darken-1">Desconto</span>
        <div class="d-flex align-center red--text text--lighten-1 font-weight-bold">
           - {{ (discount / 100) | moneyBr }}
           <v-btn icon x-small color="grey" class="ml-1" @click="$emit('open-discount')">
              <v-icon x-small>{{ icons.mdiPencil }}</v-icon>
           </v-btn>
        </div>
      </div>

      <div class="d-flex justify-space-between mb-4">
        <span class="grey--text text--darken-1">Arredondamento</span>
        <span class="font-weight-bold">{{ 0 | moneyBr }}</span>
      </div>

      <v-divider class="mb-4"></v-divider>

      <div class="d-flex justify-space-between align-end">
        <span class="text-h6 font-weight-bold">Total a Pagar</span>
        <span class="text-h5 font-weight-black success--text text--darken-1">{{ (total / 100) | moneyBr }}</span>
      </div>
    </div>

    <!-- Payment Methods -->
    <div class="mb-6">
      <div class="text-overline grey--text mb-2">Método de Pagamento</div>
      <v-item-group v-model="internalPaymentMethod" mandatory class="d-flex justify-space-between">
         <v-item v-for="method in methods" :key="method.value" v-slot="{ active, toggle }">
            <v-card
              :color="active ? 'primary' : 'white'"
              :style="active ? 'opacity: 0.95;' : ''"
              class="d-flex flex-column align-center justify-center py-3 px-2 flex-grow-1 mx-1 border-all"
              :class="{ 'border-primary': active }"
              flat
              @click="toggle"
              width="30%"
            >
              <v-icon :color="active ? 'white' : 'grey'">{{ method.icon }}</v-icon>
              <span class="caption font-weight-bold mt-1" :class="active ? 'white--text' : 'grey--text'">
                {{ method.text }}
              </span>
            </v-card>
         </v-item>
      </v-item-group>
    </div>
    
    <!-- Invoice Switch -->
     <div class="mb-4 d-flex align-center justify-space-between bg-gray-50 pa-2 rounded">
        <span class="text-body-2 grey--text text--darken-2">Emitir NFC-e?</span>
        <v-switch v-model="emitInvoice" dense hide-details class="mt-0 pt-0" color="success"></v-switch>
     </div>

    <!-- Actions -->
    <div class="mt-auto">
      <v-btn 
        block 
        color="success" 
        class="mb-3 py-6 rounded-lg font-weight-bold text-body-1 shadow-success"
        :disabled="total <= 0"
        @click="confirmPayment"
      >
        <v-icon left>{{ icons.mdiCheckCircleOutline }}</v-icon>
        Efetuar Pagamento
      </v-btn>

      <div class="d-flex">
         <v-btn 
            color="warning" 
            class="flex-grow-1 mr-2 rounded-lg elevation-0 white--text"
            depressed
         >
            <v-icon left>{{ icons.mdiPause }}</v-icon> Aguardar
         </v-btn>
         <v-btn 
            color="error" 
            class="flex-grow-1 ml-2 rounded-lg elevation-0 bg-red-opacity"
            text
            @click="$emit('cancel')"
         >
            <v-icon left>{{ icons.mdiClose }}</v-icon> Cancelar
         </v-btn>
      </div>
    </div>

  </div>
</template>

<script>
import {
  mdiAccountPlus,
  mdiAccount,
  mdiPencil,
  mdiCash,
  mdiCreditCardOutline,
  mdiQrcodeScan,
  mdiCheckCircleOutline,
  mdiPause,
  mdiClose
} from '@mdi/js';

export default {
  name: "POSInvoiceSidebar",
  props: {
    selectedCustomer: Object,
    customerResults: Array,
    customerLoading: Boolean,
    subtotal: Number,
    discount: Number,
    total: Number
  },
  data() {
    return {
      customerSearchInput: null,
      showCustomerSearch: false,
      internalPaymentMethod: 0, // index of methods
      emitInvoice: true,
      methods: [
        { text: 'Dinheiro', value: 'money', icon: mdiCash },
        { text: 'Cartão', value: 'card', icon: mdiCreditCardOutline },
        { text: 'PIX', value: 'pix', icon: mdiQrcodeScan }
      ],
      icons: {
        mdiAccountPlus,
        mdiAccount,
        mdiPencil,
        mdiCheckCircleOutline,
        mdiPause,
        mdiClose
      }
    };
  },
  computed: {
    invoiceNumber() {
      return String(Math.floor(Math.random() * 99999) + 1).padStart(5, '0');
    }
  },
  watch: {
    customerSearchInput(val) {
      this.$emit('search-customer', val);
    }
  },
  methods: {
    focusCustomerSearch() {
      this.showCustomerSearch = true;
      this.$nextTick(() => {
        if (this.$refs.customerAutocomplete) {
          this.$refs.customerAutocomplete.focus();
        }
      });
    },
    onCustomerSelect(customer) {
      this.$emit('update:selectedCustomer', customer);
      this.showCustomerSearch = false;
    },
    confirmPayment() {
       const methodObj = this.methods[this.internalPaymentMethod];
       this.$emit('request-payment', {
          method: methodObj.value,
          emit_invoice: this.emitInvoice
       });
    }
  }
};
</script>

<style scoped>
.pos-invoice-sidebar {
  border: 1px solid #e0e0e0;
}
.border-all {
  border: 1px solid #e0e0e0;
}
.border-primary {
  border-color: #1976d2 !important; /* Standard primary blue, or use var(--v-primary-base) if available */
  border-width: 2px;
}
.bg-gray-50 {
  background-color: #fafafa;
}
.shadow-success {
  box-shadow: 0 4px 14px 0 rgba(76, 175, 80, 0.39) !important;
}
.bg-red-opacity {
    background-color: rgba(244, 67, 54, 0.1);
}
.pointer { cursor: pointer; }
</style>
